import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import type { FlightsClassTypes } from 'constants/flight'
// Hooks
import { useCalendarItemData } from '../hooks/useCalendarItemData'
import type { CalendarItemBundleData } from '../hooks/types'

// Utils
import { getInventoryMessage } from '../hooks/utils'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  onSelect: (option: TripOption) => void
  prefetchedBundleData?: CalendarItemBundleData
}

// Styled Components - Mobile specific layout
const DateCard = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const SelectButton = styled(TextButton)`
  min-width: ${rem(80)};
`

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesDirectSearchFlightCalendarItemMobile(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    prefetchedBundleData,
  } = props

  const intl = useIntl()
  const { isKidsFlyFreeDate } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  const fetchedBundleData = useCalendarItemData({
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    disabled: !!prefetchedBundleData,
  })

  const bundleData = prefetchedBundleData ?? fetchedBundleData

  // Select price based on priceMode
  const regularPrice = priceMode === 'perPerson' ? bundleData.pricePerPerson : bundleData.totalPrice
  const memberPrice = priceMode === 'perPerson' ? bundleData.memberPricePerPerson : bundleData.memberTotalPrice

  const isMemberPrice = luxPlusEnabled && !!memberPrice
  const displayPrice = isMemberPrice && memberPrice ? memberPrice : regularPrice

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMa',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const selectText = intl.formatMessage({
    id: 'eMLRNdmnnD',
    defaultMessage: 'Select',
    description: 'Button label to select a charter flight date',
  })
  const unavailableText = intl.formatMessage({
    id: 'MkdIPUtrVE',
    defaultMessage: 'Unavailable',
    description: 'Button label when charter flight date is unavailable',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgv',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const nightsAndFlightsText = intl.formatMessage(
    {
      id: 'r484bkJ/Ln',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
      description: 'Label showing number of nights and flights included in charter bundle',
    },
    { nights },
  )
  const dayOffsetText = intl.formatMessage({
    id: 'l9QfkSZq5T',
    defaultMessage: '+1',
    description: 'Day offset indicator showing arrival is next day for charter flight',
  })

  // Render price display
  let priceDisplay
  if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="neutral-three">
        {loadingText}
      </BodyText>
    )
  } else if (displayPrice > 0) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'ZYDr2KDqmG',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in Maldives calendar',
    })
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '1jXTQ1zI3J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar mobile item',
        })}
        </PriceRowPriceCaption>
        <div>
          <FormattedMessage
            id="eEOhCaC58x"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives flight calendar mobile"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'neutral-two'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        {unavailableText}
      </BodyText>
    )
  }

  // Render select button
  const selectButton = isSelected ? (
    <SelectButton
      kind="primary"
      nonInteractable
      tabIndex={-1}
    >
      {selectedText}
    </SelectButton>
  ) : (
    <SelectButton
      kind="secondary"
      onClick={() => onSelect(option)}
      disabled={!bundleData.available}
    >
      {bundleData.available ? selectText : unavailableText}
    </SelectButton>
  )

  return (
    <DateCard>
      <VerticalSpacer gap={12}>
        {/* Dates Row */}
        <DateRangeContainer>
          <div>
            <BodyText variant="small" colour="neutral-three">
              {moment(departDate).format('dddd')}
            </BodyText>
            <Heading variant="heading5" wrap="no-wrap">
              {moment(departDate).format('DD MMM YYYY')}
            </Heading>
          </div>
          <Group direction="vertical" gap={0} horizontalAlign="center">
            <LineArrowRightIcon size="M" />
            <BodyText variant="small" colour="neutral-three">
              {nightsAndFlightsText}
            </BodyText>
          </Group>
          <Group direction="vertical" horizontalAlign="end">
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              <BodyText variant="small" colour="neutral-three">
                {moment(checkOut).format('dddd')}
              </BodyText>
              <Heading variant="heading5" wrap="no-wrap">
                {moment(checkOut).format('DD MMM YYYY')}<SmallSup>{dayOffsetText}</SmallSup>
              </Heading>
            </Group>
          </Group>
        </DateRangeContainer>

        {/* Kids Fly Free + Availability Warning */}
        {(() => {
          const inventoryMessage = getInventoryMessage(intl, bundleData.seatsLeft, bundleData.roomsLeft)

          return (
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              {inventoryMessage !== '' && <Group direction="horizontal" verticalAlign="center" gap={4}>
                <SolidExclamationCircleIcon colour="urgency" size="XS" />
                <BodyText variant="small" colour="critical">
                  {inventoryMessage}
                </BodyText>
              </Group>}
              {isKidsFlyFreeDate(departDate) && <KidsFlyFreeLabel variant="short" />}
            </Group>
          )
        })()}

        {/* Price and Select Button */}
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
          <VerticalSpacer gap={4}>
            {priceDisplay}
          </VerticalSpacer>
          {selectButton}
        </Group>
      </VerticalSpacer>
    </DateCard>
  )
}

export default MaldivesDirectSearchFlightCalendarItemMobile
